<template>
  <div
      id="app"
      :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view/>
    </component>

    <scroll-to-top v-if="enableScrollToTop"/>

    <Spinner/>
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'
import Spinner from "@/components/Spinner";
// This will be populated in `beforeCreate` hook
import {$themeBreakpoints, $themeColors, $themeConfig} from '@themeConfig'
import {provideToast} from 'vue-toastification/composition'
import {watch} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import {useCssVar, useWindowSize} from '@vueuse/core'

import store from '@/store'
import {createNamespacedHelpers} from 'vuex'
import axios from "axios";

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')

const {mapMutations} = createNamespacedHelpers('appConfig')
const {mapActions} = createNamespacedHelpers('user')

export default {
  components: {

    // Layouts
    LayoutVertical,
    ScrollToTop,

    // Spinner
    Spinner
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    // ...mapGetters([
    //  'getUserData'
    // ]),
    layout() {
      if (this.contentLayoutType === 'none') {
        return 'div'
      }
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      // console.log(this.getUserData.data)
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const {isRTL} = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const {skin, skinClasses} = useAppConfig()
    const {enableScrollToTop} = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const {width: windowWidth} = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  methods: {
    ...mapActions(['getUser']),
    ...mapMutations([
      'UPDATE_LAYOUT_TYPE'
    ]),
  },
  watch: {
    '$route'(to) {
      this.UPDATE_LAYOUT_TYPE(to.meta.layout);
    }
  },
  created() {
    this.getUser()
        .then((response) => {
          this.$store.dispatch('materials/getAppointments');
          this.$store.dispatch('materials/getLevels');
          this.$store.dispatch('materials/getTypes');
          if (this.$router.history._startLocation !== '/login') {
            this.$router.push(this.$router.history._startLocation);
          } else {
            this.$router.push('/');
          }
        })
        .catch((err) => {
          this.$router.push(this.$router.history._startLocation);
        });
  },
}
</script>

<style lang="scss">

.form-group {
  .v-select {
    background-color: #fff;
    border: 1px solid #d8d6de;
  }
}

@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>

